import { createEffect, createMemo, createUniqueId, For, onCleanup, onMount } from "solid-js"
import { usePlatform } from "../runtime/platform/platform"
import { createTangentMotion, stepTangentMotion, tangentPointer, tangentSegment } from "./tangent-motion"
import type { TangentPointer } from "./tangent-motion"
import "./tangent-motion.css"

export function SignInAtmosphere(props: { loading?: boolean; active?: boolean; subdued?: boolean } = {}) {
  const id = createUniqueId()
  const platform = usePlatform()
  // JSX prop getters can create memos. Resolve them under this component's
  // owner; pointer, observer and animation callbacks only read stable values.
  const active = createMemo(() => props.active !== false)
  const loading = createMemo(() => props.loading === true)
  const subdued = createMemo(() => props.subdued === true)
  const motion = createTangentMotion()
  let artwork!: HTMLDivElement
  let svg!: SVGSVGElement
  const paths: SVGPathElement[] = []

  onMount(() => {
    const reduced = matchMedia("(prefers-reduced-motion: reduce)")
    const fine = matchMedia("(hover: hover) and (pointer: fine)")
    const control = {
      frame: 0,
      time: 0,
      visible: false,
      intersecting: true,
      disposed: false,
      native: false,
      unsubscribe: undefined as (() => void) | undefined,
      pointer: { x: 0, y: 0, phi: 0, pull: 0, inside: false } as TangentPointer,
    }
    const enabled = () => !control.disposed && active() && control.visible && !reduced.matches && !document.hidden
    const draw = () => {
      const center = { x: 289 + motion.x * 4, y: 303 + motion.y * 3 }
      motion.lines.forEach((line, i) => {
        const angle = line.base + line.offset
        const segment = tangentSegment(center, angle)
        paths[i].setAttribute("d", segment ? `M${segment.a.x} ${segment.a.y}L${segment.b.x} ${segment.b.y}` : "")
        paths[i].setAttribute(
          "opacity",
          String(Math.min(0.85, 0.38 + 0.27 * (0.5 + 0.5 * Math.sin(line.base + 0.35)) + line.heat * 0.21)),
        )
        paths[i].dataset.angle = String(angle)
        paths[i].dataset.offset = String(line.offset)
      })
    }
    const tick = (now: number) => {
      control.frame = 0
      if (!enabled()) return
      const changing = stepTangentMotion(motion, (now - control.time) / 1000, {
        pointer: control.pointer,
        loading: loading(),
        subdued: subdued(),
      })
      control.time = now
      draw()
      artwork.dataset.response = changing ? "responding" : "settled"
      artwork.dataset.looping = String(loading())
      if (changing) control.frame = requestAnimationFrame(tick)
    }
    const schedule = () => {
      if (!enabled() || control.frame) return
      control.time = performance.now()
      artwork.dataset.response = "responding"
      control.frame = requestAnimationFrame(tick)
    }
    const target = (x: number, y: number) => {
      if (!enabled() || !fine.matches || !Number.isFinite(x) || !Number.isFinite(y)) return
      const matrix = svg.getScreenCTM()
      if (!matrix) return
      control.pointer = tangentPointer(new DOMPoint(x, y).matrixTransform(matrix.inverse()))
      schedule()
    }
    const move = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return
      target(event.clientX, event.clientY)
    }
    const leave = () => {
      if (!enabled() || control.native) return
      control.pointer = { ...control.pointer, x: 0, y: 0, pull: 0, inside: false }
      schedule()
    }
    const sync = () => {
      const rect = artwork.getBoundingClientRect()
      control.visible =
        control.intersecting && rect.width > 0 && rect.height > 0 && getComputedStyle(artwork).visibility !== "hidden"
      artwork.dataset.interaction = enabled() && fine.matches ? "pointer" : "static"
      if (!enabled()) {
        if (control.frame) cancelAnimationFrame(control.frame)
        control.frame = 0
        artwork.dataset.response = "settled"
        artwork.dataset.looping = "false"
      }
      if (!enabled() || !fine.matches) {
        const unsubscribe = control.unsubscribe
        control.unsubscribe = undefined
        control.native = false
        unsubscribe?.()
      }
      if (enabled() && fine.matches && !control.unsubscribe) {
        control.unsubscribe = platform.subscribePointer?.((point) => {
          if (!enabled() || !fine.matches) return
          if (!point) {
            control.native = false
            leave()
            return
          }
          if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return
          control.native = true
          // Native input is normalized against the content window, not the SVG.
          target(((point.x + 1) / 2) * innerWidth, ((point.y + 1) / 2) * innerHeight)
        })
      }
      schedule()
    }
    const resize = new ResizeObserver(sync)
    const intersection = new IntersectionObserver((entries) => {
      control.intersecting = entries[0].isIntersecting
      sync()
    })
    resize.observe(artwork)
    intersection.observe(artwork)
    window.addEventListener("pointermove", move, { passive: true })
    document.documentElement.addEventListener("pointerleave", leave)
    document.addEventListener("visibilitychange", sync)
    reduced.addEventListener("change", sync)
    fine.addEventListener("change", sync)
    draw()
    createEffect(() => {
      active()
      loading()
      subdued()
      sync()
    })
    onCleanup(() => {
      control.disposed = true
      control.unsubscribe?.()
      if (control.frame) cancelAnimationFrame(control.frame)
      resize.disconnect()
      intersection.disconnect()
      window.removeEventListener("pointermove", move)
      document.documentElement.removeEventListener("pointerleave", leave)
      document.removeEventListener("visibilitychange", sync)
      reduced.removeEventListener("change", sync)
      fine.removeEventListener("change", sync)
    })
  })

  return (
    <div
      ref={artwork}
      class="sair-tangent"
      data-testid="sair-atmosphere"
      data-response="settled"
      data-interaction="static"
      data-looping="false"
      aria-hidden="true"
    >
      <svg ref={svg} viewBox="0 0 600 540">
        <defs>
          <clipPath id={`${id}-triangle`}>
            <path d="M300 42 L526 444 L74 444 Z" />
          </clipPath>
        </defs>
        <g clip-path={`url(#${id}-triangle)`}>
          <For each={motion.lines}>
            {(_, i) => <path ref={(element) => (paths[i()] = element)} class="sair-tangent-line" data-tangent={i()} />}
          </For>
        </g>
      </svg>
    </div>
  )
}
